import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Icon, palette } from '@m12s/component-library';
import { useTranslation } from 'react-i18next';
import { check, falTimes } from 'lib/icons';
import { usePinVerification } from 'lib/hooks/usePinVerification';
import { getExpirationStatus, getExpirationMessage } from 'lib/utils/pinUtils';
import {
  ModalContainer,
  DisplayWrapper,
  Title,
  PinIndicatorsWrapper,
  PinDot,
  ErrorText,
  KeyboardWrapper,
  KeyboardRow,
  NumberButton,
  ActionButton,
  BackspaceButton,
  ActionButtonsWrapper,
  SubmitButton,
  SecondaryButton,
  SuccessContainer,
  SuccessCircle,
  SuccessText,
  LoadingSpinner,
  IconWrapper,
  WarningContainer,
  WarningCircle,
  WarningTitle,
  WarningMessage,
  WarningButtonsWrapper,
  BackButton,
} from './styled';

const modalStyles = {
  width: '375px',
  maxWidth: '90vw',
  padding: 0,
  borderRadius: '16px',
};

const headerStyles = {
  display: 'none',
};

const detailsStyles = {
  padding: 0,
};

const PinVerificationModal = ({ isOpen, operatorId, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [mode, setMode] = useState('verify'); // 'verify' | 'set-new' | 'confirm-new'
  const [newPin, setNewPin] = useState('');
  const [storedCurrentPin, setStoredCurrentPin] = useState('');
  const [showExpirationPrompt, setShowExpirationPrompt] = useState(false);
  const [expirationData, setExpirationData] = useState(null);

  const {
    verifyPin,
    setPin: setPinAPI,
    isLoading,
    error: verificationError,
  } = usePinVerification({ operatorId });

  const modalRef = useRef(null);
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      setStoredCurrentPin('');
    };
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen || isLoading || showSuccess || showExpirationPrompt) {
        return;
      }

      const { key } = event;

      if (key >= '0' && key <= '9') {
        event.preventDefault();
        if (pin.length < 6) {
          setPin((prevPin) => {
            return prevPin + key;
          });
          setError('');
        }
      } else if (key === 'Backspace') {
        event.preventDefault();
        setPin((prevPin) => {
          return prevPin.slice(0, -1);
        });
        setError('');
      } else if (key === 'Enter') {
        event.preventDefault();
        // handleSubmit is called via the effect below
      }
    },
    [isOpen, isLoading, showSuccess, showExpirationPrompt, pin.length]
  );

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError('');
      setShowSuccess(false);
      setMode('verify');
      setNewPin('');
      setStoredCurrentPin('');
      setShowExpirationPrompt(false);
      setExpirationData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isLoading && !showSuccess && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen, isLoading, showSuccess]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  const handleNumberInput = (number) => {
    if (pin.length < 6) {
      setPin((prevPin) => {
        return prevPin + number;
      });
      setError('');
    }
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleBackspace = () => {
    setPin((prevPin) => {
      return prevPin.slice(0, -1);
    });
    setError('');
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'set-new':
        return t('Pin Update Required. Enter your new PIN');
      case 'confirm-new':
        return t('Confirm your new PIN');
      default:
        return t('Enter PIN to verify identity');
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'set-new':
        return t('Continue');
      case 'confirm-new':
        return t('Set PIN');
      default:
        return t('Submit');
    }
  };

  const handleBack = () => {
    if (mode === 'confirm-new') {
      setMode('set-new');
      setPin(newPin);
      setError('');
    }
  };

  const handleVerifySubmit = async () => {
    if (pin.length === 0) {
      setError(t('Required'));
      return;
    }

    try {
      const result = await verifyPin(pin);

      if (result.isInitialPin) {
        setStoredCurrentPin(pin);
        setMode('set-new');
        setPin('');
        return;
      }

      if (result.valid) {
        const expirationStatus = getExpirationStatus(result.pinExpiration);

        if (
          expirationStatus === 'optional' ||
          expirationStatus === 'required'
        ) {
          setStoredCurrentPin(pin);
          setExpirationData({
            status: expirationStatus,
            expiration: result.pinExpiration,
          });
          setShowExpirationPrompt(true);
          return;
        }

        setShowSuccess(true);
        successTimeoutRef.current = setTimeout(() => {
          return onSuccess();
        }, 1500);
      } else {
        const errorMessage = result?.error || t('Invalid PIN');
        setError(
          result.delay
            ? t('Account locked. Try again in {{seconds}} seconds.', {
                seconds: result.delay / 1000,
              })
            : errorMessage
        );
      }
    } catch (err) {
      setError(verificationError || t('Network error. Please try again.'));
    }
  };

  const handleNewPinSubmit = () => {
    if (pin.length === 0) {
      setError(t('Required'));
      return;
    }

    setNewPin(pin);
    setMode('confirm-new');
    setPin('');
    setError('');
  };

  const handleConfirmPinSubmit = async () => {
    if (pin.length === 0) {
      setError(t('Required'));
      return;
    }

    if (pin !== newPin) {
      setError(t('PINs do not match. Please try again.'));
      return;
    }

    try {
      await setPinAPI(storedCurrentPin, pin);

      setStoredCurrentPin('');

      setShowSuccess(true);
      successTimeoutRef.current = setTimeout(() => {
        return onSuccess();
      }, 1500);
    } catch (err) {
      setStoredCurrentPin('');
      setError(err.message || t('Failed to set PIN. Please try again.'));
    }
  };

  const handleSubmit = () => {
    if (mode === 'verify') return handleVerifySubmit();
    if (mode === 'set-new') return handleNewPinSubmit();
    if (mode === 'confirm-new') return handleConfirmPinSubmit();
    return undefined;
  };

  const handleCancel = () => {
    setStoredCurrentPin('');
    onCancel();
  };

  const handleContinueWithExpiringPin = () => {
    setShowExpirationPrompt(false);
    setExpirationData(null);
    setStoredCurrentPin('');
    setShowSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      return onSuccess();
    }, 1500);
  };

  const handleChangeExpiredPin = () => {
    setShowExpirationPrompt(false);
    setExpirationData(null);
    setMode('set-new');
    setPin('');
  };

  const renderPinIndicators = () => {
    const dots = [];
    for (let i = 0; i < pin.length; i += 1) {
      dots.push(<PinDot key={i} />);
    }
    return <PinIndicatorsWrapper>{dots}</PinIndicatorsWrapper>;
  };

  const renderButton = (number) => {
    return (
      <NumberButton
        onClick={() => {
          return handleNumberInput(number);
        }}
        disabled={isLoading || showSuccess}
        type="button"
      >
        {number}
      </NumberButton>
    );
  };

  const renderSuccess = () => {
    return (
      <SuccessContainer>
        <SuccessCircle>
          <Icon icon={check} size="2rem" color={palette.White} />
        </SuccessCircle>
        <SuccessText>{t('Verification Successful')}</SuccessText>
      </SuccessContainer>
    );
  };

  const renderExpirationPrompt = () => {
    const isRequired = expirationData?.status === 'required';

    return (
      <WarningContainer>
        <WarningCircle $required={isRequired}>
          <span role="img" aria-label={t('Warning')}>
            ⚠️
          </span>
        </WarningCircle>
        <WarningTitle>
          {isRequired ? t('PIN Must Be Changed') : t('PIN Expiring Soon')}
        </WarningTitle>
        <WarningMessage>
          {getExpirationMessage(
            expirationData?.expiration,
            expirationData?.status,
            t
          )}
        </WarningMessage>
        <WarningButtonsWrapper>
          <SubmitButton variant="primary" onClick={handleChangeExpiredPin}>
            {t('Change PIN')}
          </SubmitButton>
          {!isRequired && (
            <SecondaryButton
              variant="tertiary"
              onClick={handleContinueWithExpiringPin}
            >
              {t('Continue')}
            </SecondaryButton>
          )}
        </WarningButtonsWrapper>
      </WarningContainer>
    );
  };

  const renderPinEntry = () => {
    return (
      <ModalContainer ref={modalRef} tabIndex={-1}>
        <IconWrapper onClick={handleCancel}>
          <Icon icon={falTimes} size="1.5rem" color={palette.Grey500} />
        </IconWrapper>

        <DisplayWrapper>
          <Title>{getModalTitle()}</Title>
          {renderPinIndicators()}
          <ErrorText $visible={!!error}>{error || 'Error'}</ErrorText>
        </DisplayWrapper>

        <KeyboardWrapper>
          <KeyboardRow>
            {renderButton('1')}
            {renderButton('2')}
            {renderButton('3')}
          </KeyboardRow>
          <KeyboardRow>
            {renderButton('4')}
            {renderButton('5')}
            {renderButton('6')}
          </KeyboardRow>
          <KeyboardRow>
            {renderButton('7')}
            {renderButton('8')}
            {renderButton('9')}
          </KeyboardRow>
          <KeyboardRow>
            <ActionButton
              onClick={handleClear}
              disabled={isLoading || showSuccess}
              type="button"
            >
              {t('Clear')}
            </ActionButton>
            {renderButton('0')}
            <BackspaceButton
              onClick={handleBackspace}
              disabled={isLoading || showSuccess}
              type="button"
            >
              ←
            </BackspaceButton>
          </KeyboardRow>
        </KeyboardWrapper>

        <ActionButtonsWrapper>
          <SubmitButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || showSuccess}
          >
            {isLoading ? <LoadingSpinner /> : getSubmitButtonText()}
          </SubmitButton>

          {mode === 'confirm-new' && (
            <BackButton
              variant="tertiary"
              onClick={handleBack}
              disabled={isLoading || showSuccess}
            >
              {t('Back')}
            </BackButton>
          )}

          <SecondaryButton
            variant="tertiary"
            onClick={handleCancel}
            disabled={isLoading || showSuccess}
          >
            {t('Cancel')}
          </SecondaryButton>
        </ActionButtonsWrapper>
      </ModalContainer>
    );
  };

  const getModalDetails = () => {
    if (showSuccess) {
      return renderSuccess();
    }
    if (showExpirationPrompt && expirationData) {
      return renderExpirationPrompt();
    }
    return renderPinEntry();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      modalStyles={modalStyles}
      headerStyles={headerStyles}
      detailsStyles={detailsStyles}
      details={getModalDetails()}
    />
  );
};

PinVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  operatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PinVerificationModal;
